// Games hub + scoreboard — ports app/(privado)/jogos/page.tsx.
//
// The old page made 13 sequential calls to build this (once globally, once per
// theme). The API now returns the whole scoreboard in one response, so this is
// a single query — and since IDEA-031/032 that response also covers the two
// timed games, so "Tudo" really is every game.

import { Link } from "react-router";
import type { GameId } from "@historia/shared/contracts";
import { Placar } from "@/components/jogos/Placar";
import { Consulta } from "@/components/ui/EstadoConsulta";
import { useTitle } from "@/components/ui/useTitle";
import { usePendingGames, useScoreboard } from "@/api/queries";

const JOGOS: { href: string; jogo: GameId; icone: string; nome: string; descricao: string }[] = [
  {
    href: "/jogos/emoji",
    jogo: "emoji",
    icone: "😶‍🌫️",
    nome: "Jogo dos emojis",
    descricao:
      "Cada um põe uma palavra em emojis. A outra adivinha contra o relógio — ganha quem for mais rápido.",
  },
  {
    href: "/jogos/memoria",
    jogo: "memoria",
    icone: "🃏",
    nome: "Jogo da memória",
    descricao:
      "Trinta e seis cartas, dezoito pares, tabuleiro novo em cada ronda. Ganha quem limpar mais depressa.",
  },
  {
    href: "/jogos/sudoku",
    jogo: "sudoku",
    icone: "🔢",
    nome: "Sudoku",
    descricao:
      "Nove por nove, regiões de três por três. Cada linha, coluna e região leva os números de 1 a 9.",
  },
  {
    href: "/jogos/wend",
    jogo: "wend",
    icone: "🔤",
    nome: "Wend",
    descricao:
      "Quatro palavras escondidas numa grelha com buracos. Juntas gastam todas as letras — ganha quem for mais rápida.",
  },
  {
    href: "/jogos/pinpoint",
    jogo: "pinpoint",
    icone: "🎯",
    nome: "Pinpoint",
    descricao:
      "Cinco pistas, uma categoria. Cada erro revela a pista seguinte — ganha quem precisar de menos palpites.",
  },
  {
    href: "/jogos/tango",
    jogo: "tango",
    icone: "🌗",
    nome: "Tango",
    descricao:
      "Sóis e luas, três de cada por linha e por coluna. Nunca três iguais seguidos, e as pistas = e × a apertar o resto.",
  },
  {
    href: "/jogos/queens",
    jogo: "queens",
    icone: "👑",
    nome: "Queens",
    descricao:
      "Uma coroa por linha, por coluna e por cor — e duas coroas nunca se tocam, nem pelo canto.",
  },
  {
    href: "/jogos/zip",
    jogo: "zip",
    icone: "🧵",
    nome: "Zip",
    descricao:
      "Um traço só, do 1 ao último número — e sem deixar nenhuma casa por encher. O difícil não é a ordem, é o tabuleiro todo.",
  },
  {
    href: "/jogos/crossclimb",
    jogo: "crossclimb",
    icone: "🪜",
    nome: "Crossclimb",
    descricao:
      "Sete palavras, cada uma a uma letra da seguinte. Acerta as do meio, põe-nas por ordem, e as duas pontas abrem-se.",
  },
  {
    href: "/jogos/shikaku",
    jogo: "shikaku",
    icone: "🧩",
    nome: "Shikaku",
    descricao:
      "Divide a grelha em rectângulos, um por pista. O clássico japonês de dividir por quadrados.",
  },
];

export function Jogos() {
  useTitle("Jogos");
  const query = useScoreboard();
  // IDEA-044 — a mesma lista que o Início e a navegação leem, para os três não
  // poderem discordar sobre o que falta jogar.
  const pendentes = usePendingGames();
  const aEspera = new Set((pendentes.data ?? []).map((p) => p.jogo));

  return (
    <main className="mx-auto max-w-2xl px-4 py-8 sm:px-5">
      <header className="mb-6 text-center">
        <h1 className="font-titulo text-3xl text-tinta-800 sm:text-4xl">
          Jogos
        </h1>
        <p className="mt-1 text-sm text-tinta-600">
          {aEspera.size > 0
            ? aEspera.size === 1
              ? "Há uma ronda à tua espera."
              : `Há ${aEspera.size} rondas à tua espera.`
            : "Brincar e desafiar-nos. Quem fará mais pontos?"}
        </p>
      </header>

      <Consulta query={query} linhas={2}>
        {(placar) => (
          <Placar
            global={placar.overall}
            porJogo={placar.byGame}
            porTema={placar.byTheme}
          />
        )}
      </Consulta>

      <section className="mt-8">
        <h2 className="mb-3 font-titulo text-xl text-tinta-800">Os jogos</h2>
        <ul className="flex flex-col gap-2">
          {JOGOS.map((jogo) => {
            const espera = aEspera.has(jogo.jogo);
            return (
              <li key={jogo.href}>
                <Link
                  to={jogo.href}
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-4 transition-colors ${
                    espera
                      ? "border-ouro/45 bg-creme-50 hover:bg-creme-100"
                      : "border-ouro/20 bg-creme-50 hover:border-ouro/40 hover:bg-creme-100"
                  }`}
                >
                  <span className="text-2xl" aria-hidden>
                    {jogo.icone}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-tinta-800">{jogo.nome}</span>
                      {/* A marca de que este é um dos que espera por ti. Texto e
                          não um ponto: a cor sozinha diria isto a quem a
                          distingue, e a palavra diz a toda a gente. */}
                      {espera && (
                        <span className="shrink-0 rounded-full bg-ouro px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-creme-50">
                          A tua vez
                        </span>
                      )}
                    </span>
                    <span className="block text-sm text-tinta-600">{jogo.descricao}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </main>
  );
}
